import api from '../services/api';
import SolanaService from '../services/solanaService';

// Cache for token metadata to avoid repeated API calls
const tokenMetadataCache = new Map();

// Function to get token metadata from Jupiter API
async function getTokenMetadata(mintAddress) {
  if (tokenMetadataCache.has(mintAddress)) {
    return tokenMetadataCache.get(mintAddress);
  }

  try {
    const response = await fetch(`https://token.jup.ag/all`);
    const tokens = await response.json();
    
    const token = tokens.find(t => t.address === mintAddress);
    if (token) {
      const metadata = {
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals
      };
      tokenMetadataCache.set(mintAddress, metadata);
      return metadata;
    }
    
    // If not found in Jupiter, return null
    tokenMetadataCache.set(mintAddress, null);
    return null;
  } catch (error) {
    console.error('Error fetching token metadata:', error);
    tokenMetadataCache.set(mintAddress, null);
    return null;
  }
}

// Updated function to get token symbol with fallback to hard-coded mapping
async function getTokenSymbolFromMint(mint) {
  // First try to get from Jupiter API
  const metadata = await getTokenMetadata(mint);
  if (metadata && metadata.symbol) {
    return metadata.symbol;
  }
  
  // Fallback to hard-coded mapping for known tokens
  const hardcodedMapping = {
    'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': 'USDC',
    'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB': 'USDT',
    'So11111111111111111111111111111111111111112': 'SOL',
    'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263': 'BONK',
    'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN': 'JUP',
    'u29pRsBVi7vPmCYsaYmtJEghPZFiWUK545QZh56pump': 'TOSHI',
    'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So': 'mSOL',
    '2otVNpcHXn9MKeDk3Zby5uanF3s7tki4toaJ3PZcXaUd': 'OSOL',
    '6p6xgHyF7AeE6TZkSmFsko444wqoP15icUSqi2jfGiPN': 'KEN',
    '63LfDmNb3MQ8mw9MtZ2To9bEA2M71kZUUGq5tiJxcqj9': 'FIDA',
    '4ev6p49EUoobwoohTaidA8yUNtztKSakPwPapqZye9Mj': 'ORCA',
    'Hax9LTgsQkze1YFychnBLtFH8gYbQKtKfWKKg2SP6gdD': 'RAY',
    '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R': 'RAY',
    'FbTHm1Ve61jeZg5bksjqeJZjAmpA9aFaeqiXCW6YsNXc': 'BONK',
    'KENJSUYLASHUMfHyy5o4Hp2FdNqZg1AsUPhfH2kYvEP': 'GRIFFAIN',
    'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm': 'BONK',
    '2zMMhcVQEXDtdE6vsFS7S7D5oUodfJHE8vd1gnBouauv': 'BONK',
    '3N2ETvNpPNAxhcaXgkhKoY1yDnQfs41Wnxsx5qNJpump': 'BONK',
    'EqeEBGHQhQy6SqeaJcnqAsNs3qaG19sdF89Xsarpump': 'BONK',
    'TRuTLu5vj4G9FKJzHqYJqhWx9V9iKGTfLJ1GZMTa5bQ': 'TRUMP'
  };
  
  return hardcodedMapping[mint] || 'Unknown';
}

const SUPPORTED_BLOCKCHAINS = [
  { 
    id: 'bitcoin', 
    name: 'Bitcoin', 
    symbol: 'BTC',
    explorerUrl: 'https://www.blockchain.com/explorer/addresses/btc/',
    explorerTxUrl: 'https://www.blockchain.com/explorer/transactions/',
    getTransactions: async (address) => {
      try {
        console.log(`Requesting Bitcoin transactions for address: ${address}`);
        const response = await api.get(`/api/bitcoin/transactions?address=${address}`);
        if (!response.data || !response.data.txs) {
          throw new Error('Invalid API response format');
        }

        // Calculate totals for Bitcoin
        let totalReceived = 0;
        let totalSent = 0;
        let totalFees = 0;
        response.data.txs.forEach(tx => {
          const outputsToMe = tx.outputs
            .filter(output => output.addresses && output.addresses.includes(address))
            .reduce((sum, output) => sum + (output.value || 0), 0);
          const inputsFromMe = tx.inputs
            .filter(input => input.addresses && input.addresses.includes(address))
            .reduce((sum, input) => sum + (input.output_value || 0), 0);
          
          if (inputsFromMe > 0) {
            totalSent += inputsFromMe;
            totalFees += (tx.fees || 0);
          }
          if (outputsToMe > 0) {
            totalReceived += outputsToMe;
          }
        });
        const totalAmount = (totalReceived - totalSent - totalFees) / 1e8;

        const transactions = response.data.txs.map(tx => {
          try {
            const isInput = tx.inputs.some(input => input.addresses && input.addresses.includes(address));
            const isOutput = tx.outputs.some(output => output.addresses && output.addresses.includes(address));
            
            let amount = 0;
            let type = 'Other';

            if (isInput && isOutput) {
              const inputAmount = tx.inputs
                .filter(input => input.addresses && input.addresses.includes(address))
                .reduce((sum, input) => sum + (input.output_value || 0), 0);
              const outputAmount = tx.outputs
                .filter(output => output.addresses && output.addresses.includes(address))
                .reduce((sum, output) => sum + (output.value || 0), 0);
              amount = (outputAmount - inputAmount) / 100000000;
              type = 'Change';
            } else if (isInput) {
              const inputAmount = tx.inputs
                .filter(input => input.addresses && input.addresses.includes(address))
                .reduce((sum, input) => sum + (input.output_value || 0), 0);
              const outputAmount = tx.outputs
                .filter(output => output.addresses && output.addresses.includes(address))
                .reduce((sum, output) => sum + (output.value || 0), 0);
              amount = (outputAmount - inputAmount) / 100000000;
              type = 'Send';
            } else if (isOutput) {
              amount = tx.outputs
                .filter(output => output.addresses && output.addresses.includes(address))
                .reduce((sum, output) => sum + (output.value || 0), 0) / 100000000;
              type = 'Receive';
            }

            const fee = tx.fees ? tx.fees / 100000000 : 0;

            let source = 'Unknown';
            let destination = 'Unknown';

            if (isOutput) {
              const firstInput = tx.inputs?.[0];
              if (firstInput && firstInput.addresses && firstInput.addresses.length > 0) {
                source = firstInput.addresses[0];
              }
              destination = address;
            } else {
              const output = tx.outputs?.find(output => 
                output.addresses && 
                output.addresses.length > 0 && 
                !output.addresses.includes(address)
              );
              if (output && output.addresses && output.addresses.length > 0) {
                destination = output.addresses[0];
              }
              source = address;
            }

            return {
              id: tx.hash,
              date: new Date(tx.received),
              type: type,
              amount: amount,
              fee: fee,
              deliveredAmount: type === 'Receive' ? amount : type === 'Send' ? Math.abs(amount) - fee : amount,
              hash: tx.hash,
              account: address,
              source: source,
              destination: destination,
              transactionType: 'Bitcoin'
            };
          } catch (error) {
            return null;
          }
        }).filter(tx => tx !== null);

        return {
          transactions,
          totalAmount
        };
      } catch (error) {
        throw new Error(`Failed to fetch Bitcoin transactions: ${error.message}`);
      }
    }
  },
  { 
    id: 'ethereum', 
    name: 'Ethereum', 
    symbol: 'ETH',
    explorerUrl: 'https://etherscan.io/address/',
    explorerTxUrl: 'https://etherscan.io/tx/',
    getTransactions: async (address) => {
      try {
        console.log(`Requesting Ethereum transactions for address: ${address}`);
        const response = await api.get(`/api/ethereum/transactions?address=${address}`);
        if (!response.data || !Array.isArray(response.data.result)) {
          throw new Error('Invalid API response format');
        }
        const txs = response.data.result;
        let total = 0;
        txs.forEach(tx => {
          const from = (tx.from || '').toLowerCase();
          const to = (tx.to || '').toLowerCase();
          const myAddr = address.toLowerCase();
          const valueEth = parseFloat(tx.value) / 1e18;
          const feeEth = (parseFloat(tx.gasUsed) * parseFloat(tx.gasPrice)) / 1e18;
          
          if (to === myAddr && from !== myAddr) {
            // Receiving
            total += valueEth;
          } else if (from === myAddr && to !== myAddr) {
            // Sending
            total -= (valueEth + feeEth);
          }
          // If sending to self, only subtract fee
          else if (from === myAddr && to === myAddr) {
            total -= feeEth;
          }
        });

        const transactions = txs.map(tx => {
          const from = (tx.from || '').toLowerCase();
          const to = (tx.to || '').toLowerCase();
          const myAddr = address.toLowerCase();
          const valueEth = parseFloat(tx.value) / 1e18;
          const feeEth = (parseFloat(tx.gasUsed) * parseFloat(tx.gasPrice)) / 1e18;
          let type = 'Other';
          let amount = 0;
          if (to === myAddr && from !== myAddr) {
            type = 'Receive';
            amount = valueEth;
          } else if (from === myAddr && to !== myAddr) {
            type = 'Send';
            amount = -valueEth;
          } else if (from === myAddr && to === myAddr) {
            type = 'Self';
            amount = 0;
          }
          return {
            id: tx.hash,
            date: new Date(tx.timeStamp * 1000),
            type,
            amount,
            fee: feeEth,
            deliveredAmount: type === 'Receive' ? valueEth : type === 'Send' ? Math.abs(valueEth) - feeEth : 0,
            hash: tx.hash,
            account: address,
            source: from,
            destination: to,
            transactionType: 'Ethereum'
          };
        });
        return {
          transactions,
          totalAmount: total
        };
      } catch (error) {
        throw new Error(`Failed to fetch Ethereum transactions: ${error.message}`);
      }
    }
  },
  { 
    id: 'solana', 
    name: 'Solana', 
    symbol: 'SOL',
    explorerUrl: 'https://solscan.io/account/',
    explorerTxUrl: 'https://solscan.io/tx/',
    getTransactions: async (address) => {
      try {
        const rawTransactions = await SolanaService.getTransactionHistory(address, 50); // Fetch last 50 transactions

        const transactions = [];
        const aggregatedAmounts = new Map();

        // Process raw transactions into a flat list for the UI
        for (const tx of rawTransactions) {
          const dateObj = new Date(tx.blockTime * 1000);

          // Process each transfer within the transaction
          for (const transfer of tx.transfers) {
            const { mint, symbol, amount, direction } = transfer;
            
            const type = direction === 'in' ? 'Receive' : 'Send';
            const transferAmount = direction === 'in' ? amount : -amount;
            
            transactions.push({
              tx_hash: tx.signature,
              type: type,
              amount: transferAmount,
              fee: 0, // Fee is handled as a separate line item below
              delivered_amount: amount,
              rate: null,
              date: dateObj,
              coin: symbol,
              mint: mint,
              source: direction === 'out' ? address : 'N/A',
              destination: direction === 'in' ? address : 'N/A'
            });

            // Aggregate amounts for logging
            const currentAggregated = aggregatedAmounts.get(symbol) || 0;
            aggregatedAmounts.set(symbol, currentAggregated + transferAmount);
          }
          
          // If there's a fee, create a separate transaction line item for it
          if (tx.fee > 0.0000001) { // Avoid tiny dust fees
          transactions.push({
              tx_hash: tx.signature,
              type: 'Fee',
              amount: -tx.fee,
              fee: tx.fee,
              delivered_amount: 0,
              rate: null,
            date: dateObj,
              coin: 'SOL',
              mint: 'So11111111111111111111111111111111111111112',
              source: address,
              destination: 'N/A'
            });
            // Also log the fee aggregation
            const currentAggregated = aggregatedAmounts.get('SOL') || 0;
            aggregatedAmounts.set('SOL', currentAggregated - tx.fee);
          }
        }
        
        // The `totalAmount` is removed from here because it was incorrect.
        // We will fetch the total balance separately to fix the UI.
        return { transactions };
        
      } catch (error) {
        // Log the full error for better debugging
        console.error(`Failed to fetch Solana transactions for address ${address}:`, error);
        throw new Error(`Failed to fetch Solana transactions: ${error.message}`);
      }
    }
  },
  { 
    id: 'xrp', 
    name: 'XRP', 
    symbol: 'XRP',
    explorerUrl: 'https://xrpscan.com/account/',
    explorerTxUrl: 'https://xrpscan.com/tx/',
    getTransactions: async (address) => {
      try {
        console.log(`Requesting XRP transactions for address: ${address}`);
        const response = await api.get(`/api/xrp/transactions?address=${address}`);
        if (!response.data || !response.data.transactions) {
          throw new Error('Invalid API response format');
        }

        const totalAmount = response.data.transactions.reduce((sum, tx) => {
          if (tx.TransactionType === 'Payment') {
            let rawAmount = tx.Amount;
            if (typeof rawAmount === 'object' && rawAmount.value) {
              rawAmount = rawAmount.value;
            }
            const amount = parseFloat(rawAmount || 0) / 1000000;
            const fee = parseFloat(tx.Fee || 0) / 1000000;

            if (tx.Account === address) {
              // If sending
              return sum - amount - fee;
            } else if (tx.Destination === address) {
              // If receiving
              return sum + amount;
            }
          }
          return sum;
        }, 0);

        const transactions = response.data.transactions.map(tx => {
          let amount = 0;
          let type = 'Other';
          
          if (tx.TransactionType === 'Payment') {
            let rawAmount = tx.Amount;
            if (typeof rawAmount === 'object' && rawAmount.value) {
              rawAmount = rawAmount.value;
            }

            if (tx.Account === address) {
              amount = -parseFloat(rawAmount || 0) / 1000000;
              type = 'Send';
            } else if (tx.Destination === address) {
              amount = parseFloat(rawAmount || 0) / 1000000;
              type = 'Receive';
            }
          }

          return {
            id: tx.hash,
            date: new Date(tx.date),
            type: type,
            amount: amount,
            fee: parseFloat(tx.Fee || 0) / 1000000,
            deliveredAmount: type === 'Receive' ? Math.abs(amount) : type === 'Send' ? Math.abs(amount) - (parseFloat(tx.Fee || 0) / 1000000) : 0,
            hash: tx.hash,
            account: tx.Account,
            destination: tx.Destination,
            transactionType: tx.TransactionType
          };
        });

        return {
          transactions,
          totalAmount
        };
      } catch (error) {
        throw new Error(`Failed to fetch XRP transactions: ${error.message}`);
      }
    }
  },
  { 
    id: 'sui', 
    name: 'Sui', 
    symbol: 'SUI',
    explorerUrl: 'https://suiexplorer.com/address/',
    explorerTxUrl: 'https://suiexplorer.com/txblock/',
    getTransactions: async (address) => {
      try {
        console.log(`Requesting Sui transactions for address: ${address}`);
        const response = await api.get(`/api/sui/transactions?address=${address}`);
        if (!response.data || !Array.isArray(response.data)) {
          throw new Error('Invalid API response format');
        }

        const txs = response.data;
        let total = 0;
        const transactions = txs.map(tx => {
          const { digest, timestamp, effects, balanceChanges = [] } = tx;
          let type = 'Other';
          let amount = 0;
          let from = '-';
          let to = '-';
          let fee = 0;

          // Process balance changes
          balanceChanges.forEach(change => {
            if (change.owner.AddressOwner === address) {
              const changeAmount = change.amount / 1e9; // Convert MIST to SUI
              if (changeAmount > 0) {
                type = 'Receive';
                amount += changeAmount;
                from = change.owner.AddressOwner;
                to = address;
              } else if (changeAmount < 0) {
                type = 'Send';
                amount += changeAmount;
                from = address;
                to = change.owner.AddressOwner;
              }
            }
          });

          // Calculate fee if available
          if (effects && effects.gasUsed) {
            fee = effects.gasUsed.computationCost / 1e9; // Convert MIST to SUI
            if (type === 'Send') {
              total -= fee;
            }
          }

          // Update total
          if (type === 'Receive') {
            total += amount;
          } else if (type === 'Send') {
            total -= amount;
          }

          return {
            id: digest,
            date: new Date(timestamp),
            type,
            amount,
            fee,
            deliveredAmount: type === 'Receive' ? amount : type === 'Send' ? Math.abs(amount) - fee : 0,
            hash: digest,
            account: address,
            source: from,
            destination: to,
            transactionType: 'Sui'
          };
        });

        return {
          transactions,
          totalAmount: total
        };
      } catch (error) {
        throw new Error(`Failed to fetch Sui transactions: ${error.message}`);
      }
    }
  }
];

export { SUPPORTED_BLOCKCHAINS, getTokenMetadata, getTokenSymbolFromMint }; 